import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { CONTACT_FORM_URL } from '@/utils/form';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') params.append(key, value);
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
        if (serverMsg?.includes('spam') || serverMsg?.includes('form data is spam')) {
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫画网',
    url: 'https://hanmanhua.com/',
    description: '免费漫画在线阅读 - 韩国漫画网站大全',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-010-8888-6666',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    sameAs: ['https://weibo.com/', 'https://bilibili.com/'],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫画网上的漫画都是免费的吗？',
        acceptedAnswer: { '@type': 'Answer', text: '是的，韩漫画网上的所有漫画都可以免费在线阅读，无需会员注册。' },
      },
      {
        '@type': 'Question',
        name: '韩漫画网每天更新多少部漫画？',
        acceptedAnswer: { '@type': 'Answer', text: '韩漫画网每天更新超过200话漫画内容，保持最新最热的韩国漫画资源。' },
      },
      {
        '@type': 'Question',
        name: '如何在手机上访问韩漫画网？',
        acceptedAnswer: { '@type': 'Answer', text: '您可以直接在手机浏览器中访问韩漫画网，网站已完美适配移动端阅读。' },
      },
      {
        '@type': 'Question',
        name: '韩漫画网支持离线下载吗？',
        acceptedAnswer: { '@type': 'Answer', text: '目前韩漫画网暂不支持离线下载功能，但我们正在努力开发中，敬请期待。' },
      },
      {
        '@type': 'Question',
        name: '如何联系韩漫画网客服？',
        acceptedAnswer: { '@type': 'Answer', text: '您可以通过本页面的联系表单或发送邮件至 support@hanmanhua.com 联系我们。' },
      },
    ],
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 韩漫画网',
    description: '了解韩漫画网的品牌故事、团队介绍、口碑评价和联系方式。免费漫画在线阅读，韩国漫画网站大全。',
    url: 'https://hanmanhua.com/about',
    isPartOf: { '@type': 'WebSite', name: '韩漫画网', url: 'https://hanmanhua.com/' },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">关于我们</h1>
            <p className="mt-2 text-sm text-foreground-500">了解韩漫画网的故事与使命</p>
          </section>

          {/* Brand Introduction */}
          <section className="bg-background-100" id="intro">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
                <div className="flex-1">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">品牌介绍</h2>
                  <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    韩漫画网成立于2024年，是国内领先的<strong>免费韩国漫画在线阅读平台</strong>。我们致力于为广大漫画爱好者提供<span className="text-primary-600 font-medium">最全的韩国漫画网站大全</span>，涵盖恋爱、动作、奇幻、悬疑、搞笑、恐怖、校园、历史等八大漫画分类。
                  </p>
                  <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                    韩漫画网始终坚持以用户体验为核心，通过不断优化阅读界面和更新漫画资源，为读者带来<strong>沉浸式的在线阅读体验</strong>。我们的漫画内容均经过精心筛选，画质精美、翻译准确，是漫画爱好者的必备工具。
                  </p>
                  <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                    成立两年来，韩漫画网已积累了超过<strong className="text-primary-600">50万活跃用户</strong>，收录超过<strong className="text-primary-600">5000部优质韩国漫画</strong>，日均更新超过200话内容，成为国内韩国漫画领域的标杆平台。
                  </p>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[380px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20studio%20office%20interior%2C%20creative%20workspace%20with%20drawing%20tablets%20and%20manga%20art%20on%20walls%2C%20warm%20orange%20and%20cream%20lighting%2C%20modern%20clean%20workspace%2C%20professional%20creative%20atmosphere%2C%20soft%20natural%20light&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="韩漫画网品牌 - 免费漫画在线阅读平台"
                    title="韩漫画网 - 韩国漫画网站大全"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Company Values */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" id="values">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
              使命与价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-heart-pulse-line',
                  title: '热爱漫画',
                  desc: '我们团队的每一位成员都是狂热的漫画爱好者，因为我们热爱，所以我们更懂读者需要什么。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '品质至上',
                  desc: '严格筛选每一部作品，确保画质清晰、翻译准确，为读者提供最优质的在线阅读体验。',
                },
                {
                  icon: 'ri-team-line',
                  title: '用户第一',
                  desc: '以用户需求为导向，持续优化产品功能，打造最受漫画爱好者欢迎的免费阅读平台。',
                },
                {
                  icon: 'ri-lightbulb-flash-line',
                  title: '创新驱动',
                  desc: '不断探索新技术，提升阅读体验，推动韩国漫画在线阅读行业的持续发展。',
                },
                {
                  icon: 'ri-hand-heart-line',
                  title: '尊重版权',
                  desc: '严格遵循版权法规，与版权方建立良好合作关系，保护原创作者的合法权益。',
                },
                {
                  icon: 'ri-global-line',
                  title: '开放共享',
                  desc: '打造开放包容的漫画社区，让更多读者能够轻松接触到优质的韩国漫画资源。',
                },
              ].map((item) => (
                <div key={item.title} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground-800">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="bg-background-100" id="reputation">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {[
                  {
                    name: '小明的漫画屋',
                    role: '资深漫画读者',
                    content: '韩漫画网是我用过最好用的韩国漫画网站大全！漫画种类超多，更新也很快，关键是全部免费，太良心了！',
                    rating: 5,
                  },
                  {
                    name: '二次元少女小芳',
                    role: '韩漫爱好者',
                    content: '画质超棒，分类也很清晰。我最爱看恋爱和奇幻类的韩国漫画，韩漫画网上面都有，强烈推荐！',
                    rating: 5,
                  },
                  {
                    name: '程序员老王',
                    role: '漫画收藏家',
                    content: '免费漫画在线阅读就选韩漫画网，界面干净整洁，没有烦人的弹窗广告，阅读体验一级棒。',
                    rating: 5,
                  },
                ].map((testimonial) => (
                  <div key={testimonial.name} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.content}</p>
                    <div className="mt-4 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-sm font-semibold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                        <p className="text-xs text-foreground-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" id="contact">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-2">
              联系我们
            </h2>
            <p className="text-center text-sm text-foreground-500 mb-8">
              有任何问题或建议？欢迎随时联系我们
            </p>

            <div className="max-w-xl mx-auto">
              <div className="flex flex-wrap gap-4 mb-8 justify-center">
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-mail-line"></i>
                  </div>
                  <span>support@hanmanhua.com</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-phone-line"></i>
                  </div>
                  <span>010-8888-6666</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <span>北京市朝阳区</span>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6"
                data-readdy-form=""
                id="about-contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                    placeholder="请输入咨询主题"
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 resize-none"
                    placeholder="请输入您的留言，最多500字"
                  ></textarea>
                  <p className="mt-1 text-xs text-foreground-400">最多500个字符</p>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  className="mt-5 w-full bg-primary-500 text-background-50 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>

                {formStatus === 'success' && (
                  <p className="mt-3 text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === 'error' && (
                  <p className="mt-3 text-sm text-red-600 text-center">{formError || '提交失败，请稍后再试'}</p>
                )}
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background-100" id="faq">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  { q: '韩漫画网上的漫画都是免费的吗？', a: '是的，韩漫画网上的所有漫画都可以免费在线阅读，无需会员注册，也没有隐藏费用。' },
                  { q: '韩漫画网每天更新多少部漫画？', a: '韩漫画网每天更新超过200话漫画内容，确保读者能够及时阅读到最新最热的韩国漫画资源。' },
                  { q: '如何在手机上访问韩漫画网？', a: '您可以直接在手机浏览器中访问韩漫画网，我们的网站已完美适配移动端，提供流畅的手机阅读体验。' },
                  { q: '韩漫画网支持离线下载吗？', a: '目前韩漫画网暂不支持离线下载功能，但我们正在努力开发中，预计未来版本会上线此功能。' },
                  { q: '如何联系韩漫画网客服？', a: '您可以通过本页面的联系表单、发送邮件至 support@hanmanhua.com 或拨打电话 010-8888-6666 联系我们。' },
                ].map((item) => (
                  <details key={item.q} className="group bg-background-50 rounded-lg border border-background-200/70">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h3 className="text-sm font-medium text-foreground-800 pr-4">{item.q}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}